import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { api, type Client, type Node } from '../lib/api'
import { PageHeader, Button, Table, Modal, Field, Input, Select, Badge, ErrorBox } from '../components/ui'
import { IconPlus } from '../components/icons'
import { RowMenu } from '../components/RowMenu'
import { TaskProgress } from '../components/TaskProgress'
import { FilterBar, FilterSelect, SearchInput } from '../components/Filters'

export function ClientsPage() {
  const qc = useQueryClient()
  const [addOpen, setAddOpen] = useState(false)
  const [taskId, setTaskId] = useState<string | null>(null)

  const { data: clients = [] } = useQuery({ queryKey: ['clients'], queryFn: async () => (await api.get<Client[]>('/clients')).data })
  const { data: nodes = [] } = useQuery({ queryKey: ['nodes'], queryFn: async () => (await api.get<Node[]>('/nodes')).data })
  const nodeMap = Object.fromEntries(nodes.map(n => [n.id, n]))

  const [fNode, setFNode] = useState('')
  const [fSearch, setFSearch] = useState('')
  const filtered = useMemo(() => {
    let list = clients
    if (fNode) list = list.filter(c => c.node_id === fNode)
    if (fSearch) {
      const q = fSearch.toLowerCase()
      list = list.filter(c => c.unix_user.toLowerCase().includes(q) ||
        (c.full_name || '').toLowerCase().includes(q) ||
        (c.email || '').toLowerCase().includes(q))
    }
    return list
  }, [clients, fNode, fSearch])

  const suspend = useMutation({
    mutationFn: async (id: number) => (await api.post(`/clients/${id}/suspend`)).data,
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ['clients'] })
      if (r?.tasks?.length) setTaskId(r.tasks[0])
    },
  })
  const resume = useMutation({
    mutationFn: async (id: number) => (await api.post(`/clients/${id}/resume`)).data,
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ['clients'] })
      if (r?.tasks?.length) setTaskId(r.tasks[0])
    },
  })

  const del = useMutation({
    mutationFn: async (id: number) => api.delete(`/clients/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  })
  const setSSHPass = useMutation({
    mutationFn: async ({ id, password }: { id: number; password: string }) =>
      (await api.post(`/clients/${id}/ssh-password`, { password })).data,
    onSuccess: (r: any) => { if (r?.task_id) setTaskId(r.task_id) },
  })
  const setQuota = useMutation({
    mutationFn: async ({ id, mb }: { id: number; mb: number }) =>
      (await api.put(`/clients/${id}/quota`, { disk_quota_mb: mb })).data,
    onSuccess: (r: any) => { if (r?.task_id) setTaskId(r.task_id); qc.invalidateQueries({ queryKey: ['clients'] }) },
  })


  return (
    <>
      <PageHeader
        title="Клиенты"
        subtitle="Каждый клиент — отдельный системный пользователь на ноде. Сайты, базы, FTP хранятся в /var/www/<клиент>."
        actions={<Button onClick={() => setAddOpen(true)}><IconPlus /> Добавить клиента</Button>}
      />
      <FilterBar>
        <SearchInput value={fSearch} onChange={setFSearch} placeholder="Логин / имя / email…" className="min-w-[240px] flex-1" />
        <FilterSelect value={fNode} onChange={setFNode} options={[
          { value: '', label: 'Все ноды' },
          ...nodes.map(n => ({ value: n.id, label: n.name })),
        ]} />
      </FilterBar>
      <div className="text-[12px] text-neutral-500 mb-2">
        {filtered.length !== clients.length ? `Найдено: ${filtered.length} из ${clients.length}` : `Всего клиентов: ${clients.length}`}
      </div>
      <Table<Client>
        rows={filtered}
        empty={clients.length === 0 ? 'Пока нет клиентов.' : 'Ничего не найдено.'}
        cols={[
          { key: 'user', label: 'Логин', mobileLabel: 'Логин',
            render: (c) => <span className="font-mono font-medium text-neutral-900">{c.unix_user}</span> },
          { key: 'name', label: 'Имя', render: (c) => <span className="text-neutral-600 text-[13px]">{c.full_name || '—'}</span> },
          { key: 'email', label: 'Email', render: (c) => <span className="text-neutral-600 text-[13px]">{c.email || '—'}</span> },
          { key: 'node', label: 'Нода', render: (c) => <span className="text-neutral-600 text-[13px]">{nodeMap[c.node_id]?.name || c.node_id.slice(0, 8)}</span> },
          { key: 'home', label: 'Домашняя папка', render: (c) => <span className="font-mono text-[12px] text-neutral-600">{c.home_dir}</span> },
          { key: 'st', label: 'Статус',
            render: (c) => c.is_suspended
              ? <Badge tone="yellow">заморожен</Badge>
              : <Badge tone="green">активен</Badge> },
          { key: 'act', label: '', w: '1%',
            render: (c) => (
              <div className="flex justify-end">
                <RowMenu items={[
                  { label: 'Сменить SSH-пароль', onClick: () => {
                    const p = prompt(`Новый SSH-пароль для ${c.unix_user}?`)
                    if (p && p.length >= 8) setSSHPass.mutate({ id: c.id, password: p })
                  }},
                  { label: 'Квота диска', onClick: () => {
                    const cur = (c as any).disk_quota_mb || 0
                    const s = prompt(`Квота (MB), 0 = без лимита`, String(cur))
                    if (s === null) return
                    const mb = parseInt(s, 10)
                    if (Number.isFinite(mb) && mb >= 0) setQuota.mutate({ id: c.id, mb })
                  }},
                  { divider: true, label: '', onClick: () => {} },
                  c.is_suspended
                    ? { label: 'Разморозить все сайты', onClick: () => resume.mutate(c.id) }
                    : { label: 'Заморозить (все сайты)',
                        onClick: () => confirm(`Заморозить ${c.unix_user}?`) && suspend.mutate(c.id) },
                  { divider: true, label: '', onClick: () => {} },
                  { label: 'Удалить', danger: true,
                    onClick: () => confirm(`Удалить ${c.unix_user}?`) && del.mutate(c.id) },
                ]} />
              </div>
            )},
        ]}
      />
      {addOpen && <AddClientModal nodes={nodes} onClose={() => setAddOpen(false)}
        onTask={(t) => { setAddOpen(false); setTaskId(t); qc.invalidateQueries({ queryKey: ['clients'] }) }} />}
      {taskId && <TaskProgress taskId={taskId} onClose={() => setTaskId(null)} />}
    </>
  )
}

function genPass(len = 20): string {
  const chars = 'abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  let s = ''
  const rand = new Uint32Array(len); crypto.getRandomValues(rand)
  for (let i = 0; i < len; i++) s += chars[rand[i] % chars.length]
  return s
}

function AddClientModal({ nodes, onClose, onTask }: { nodes: Node[]; onClose: () => void; onTask: (id: string) => void }) {
  const [f, setF] = useState({ node_id: nodes[0]?.id || '', unix_user: '', full_name: '', email: '', ssh_password: genPass() })
  const [err, setErr] = useState('')
  const [created, setCreated] = useState<{ unix_user: string; password: string } | null>(null)
  const create = useMutation({
    mutationFn: async () => (await api.post('/clients', f)).data,
    onSuccess: (r: any) => {
      setCreated({ unix_user: f.unix_user, password: f.ssh_password })
      onTask(r.task_id)
    },
    onError: (e: any) => setErr(e?.response?.data?.detail || e.message),
  })

  if (created) {
    return (
      <Modal open onClose={onClose} title="Клиент создан">
        <div className="space-y-3">
          <div className="text-[13px] text-neutral-700">Сохраните доступы — пароль больше не покажем в открытом виде:</div>
          <div className="p-3 bg-neutral-50 border border-neutral-200 rounded-md font-mono text-[13px]">
            <div>Логин: <b>{created.unix_user}</b></div>
            <div className="flex items-center justify-between gap-3 mt-1">
              <span>Пароль: <b>{created.password}</b></span>
              <Button size="sm" variant="secondary" onClick={() => navigator.clipboard.writeText(created.password)}>Копировать</Button>
            </div>
            <div className="mt-1 text-neutral-500 text-[11px]">SSH: <code>ssh {created.unix_user}@{nodes.find(n => n.id === f.node_id)?.ip_address}</code></div>
          </div>
          <div className="flex justify-end pt-2">
            <Button onClick={onClose}>Готово</Button>
          </div>
        </div>
      </Modal>
    )
  }

  return (
    <Modal open onClose={onClose} title="Добавить клиента">
      <form onSubmit={(e) => { e.preventDefault(); create.mutate() }} className="space-y-4">
        <Field label="Нода">
          <Select value={f.node_id} onChange={(e) => setF({ ...f, node_id: e.target.value })}>
            {nodes.map(n => <option key={n.id} value={n.id}>{n.name} — {n.hostname}</option>)}
          </Select>
        </Field>
        <Field label="Логин (unix)" hint="Буквы, цифры, дефис/подчёркивание, ≤ 32 символа">
          <Input value={f.unix_user} onChange={(e) => setF({ ...f, unix_user: e.target.value })} placeholder="myclient" required autoFocus />
        </Field>
        <Field label="Имя (необязательно)"><Input value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} /></Field>
        <Field label="Email (необязательно)"><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
        <Field label="SSH-пароль" hint="Сгенерирован автоматически. С ним клиент сможет зайти по SSH/SFTP.">
          <div className="flex gap-2">
            <Input value={f.ssh_password} onChange={(e) => setF({ ...f, ssh_password: e.target.value })} className="font-mono" />
            <Button variant="secondary" type="button" onClick={() => setF({ ...f, ssh_password: genPass() })}>↻</Button>
          </div>
        </Field>
        {err && <ErrorBox>{err}</ErrorBox>}
        <div className="flex justify-end gap-2 pt-4 border-t border-neutral-100 -mx-5 px-5">
          <Button variant="ghost" onClick={onClose}>Отмена</Button>
          <Button type="submit" disabled={create.isPending}>{create.isPending ? 'Создаём…' : 'Создать'}</Button>
        </div>
      </form>
    </Modal>
  )
}
